import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, Trophy, Play, Award, User } from 'lucide-react';
import './MatchDetails.css';
import tournamentData from '../../data/tournamentData.json';

const MatchDetails = () => {
  console.log('MatchDetails component rendering...');
  console.log('tournamentData:', tournamentData);
  
  // Tournament info from JSON
  const [tournamentInfo] = useState(tournamentData.tournamentInfo);

  // Players data from JSON
  const players = tournamentData.players;

  // Helper function to find player by ID
  const findPlayerById = (id: number) => {
    return players.find((player: any) => player.id === id);
  };

  // Helper function to resolve player references in matches
  const resolveMatches = (matchesData: any) => {
    const resolvedMatches: any = {};
    
    Object.keys(matchesData).forEach(roundKey => {
      resolvedMatches[roundKey] = matchesData[roundKey].map((match: any) => ({
        ...match,
        player1: match.player1 ? findPlayerById(match.player1) : null,
        player2: match.player2 ? findPlayerById(match.player2) : null,
        winner: match.winner ? findPlayerById(match.winner) : null,
        currentPlayer: match.currentPlayer ? findPlayerById(match.currentPlayer) : null
      }));
    });
    
    return resolvedMatches;
  };

  // Complete tournament bracket from JSON
  console.log('Resolving matches...');
  const resolvedMatches = resolveMatches(tournamentData.matches);
  console.log('Resolved matches:', resolvedMatches);
  const [matches] = useState(resolvedMatches);

  const [selectedRound, setSelectedRound] = useState(1);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyMessage, setPrivacyMessage] = useState('');

  const getRoundName = (round: number) => {
    switch(round) {
      case 1: return "Round 1 (First Round)";
      case 2: return "Round 2 (Second Round)"; 
      case 3: return "Round 3 (Quarter Final)";
      case 4: return "Round 4 (Semi Final)";
      case 5: return "Round 5 (Final)";
      default: return `Round ${round}`;
    }
  };

  const getRoundDescription = (round: number) => {
    switch(round) {
      case 1: return "20 players → 10 matches → 10 winners";
      case 2: return "10 players → 5 matches → 5 winners"; 
      case 3: return "5 players → 2 matches + 1 bye → 3 players";
      case 4: return "3 players → 1 match + 1 bye → 2 players";
      case 5: return "2 players → 1 match → Champion";
      default: return "";
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'ongoing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'waiting': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed': return <Trophy className="w-4 h-4" />;
      case 'ongoing': return <Play className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'waiting': return <User className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch(status) {
      case 'completed': return 'Completed';
      case 'ongoing': return 'Live Now';
      case 'next': return 'Next Match';
      case 'pending': return 'Scheduled';
      case 'waiting': return 'Awaiting';
      default: return 'Pending';
    }
  };

  const handlePlayerClick = (player: any) => {
    if (!player) return;
    
    // Check profile visibility
    const currentUser = { id: 999, name: "Current User", friends: [1, 2, 3, 5, 8, 10, 14, 17, 20] }; // Mock current user
    
    if (player.profileVisibility === "public") {
      setSelectedPlayer(player);
      setShowProfileModal(true);
    } else if (player.profileVisibility === "friends" && currentUser.friends.includes(player.id)) {
      setSelectedPlayer(player);
      setShowProfileModal(true);
    } else if (player.profileVisibility === "private") {
      setPrivacyMessage(`${player.name}'s profile is private. Only they can view their profile.`);
      setShowPrivacyModal(true);
    } else {
      setPrivacyMessage(`${player.name}'s profile is only visible to friends.`);
      setShowPrivacyModal(true);
    }
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedPlayer(null);
  };

  const closePrivacyModal = () => {
    setShowPrivacyModal(false);
    setPrivacyMessage('');
  };

  const getStatusBadge = (status: string) => {
    const statusClass = `status-badge status-${status}`;
    const statusText = getStatusText(status);
    
    return (
      <span className={statusClass}>
        {status === 'ongoing' && '🔴'}
        {status === 'completed' && '✅'}
        {status === 'next' && '⏭️'}
        {status === 'pending' && '⏳'}
        {status === 'waiting' && '⏸️'}
        {statusText}
      </span>
    );
  };

  const getCurrentRoundMatches = () => {
    console.log('getCurrentRoundMatches called with selectedRound:', selectedRound);
    console.log('matches object:', matches);
    
    switch(selectedRound) {
      case 1: 
        console.log('Returning round1:', matches.round1);
        return matches.round1;
      case 2: 
        console.log('Returning round2:', matches.round2);
        return matches.round2;
      case 3: 
        console.log('Returning round3:', matches.round3);
        return matches.round3;
      case 4: 
        console.log('Returning round4:', matches.round4);
        return matches.round4;
      case 5: 
        console.log('Returning round5:', matches.round5);
        return matches.round5;
      default: 
        console.log('Returning empty array for default case');
        return [];
    }
  };

  // Enhanced Excel-style table view with icons and player details
  const renderTable = () => {
    const currentMatches = getCurrentRoundMatches();
    console.log('renderTable - currentMatches:', currentMatches);
    
    const getPlayerRank = (player: any) => {
      if (!player) return null;
      const sortedPlayers = [...players].sort((a: any, b: any) => b.rating - a.rating);
      return sortedPlayers.findIndex((p: any) => p.id === player.id) + 1;
    };

    const getRankIcon = (rank: number) => {
      if (rank === 1) return "🥇";
      if (rank === 2) return "🥈";
      if (rank === 3) return "🥉";
      if (rank <= 5) return "🏆";
      if (rank <= 10) return "⭐";
      return "👤";
    };

    const getWinnerIcon = (status: string, winner: any) => {
      if (winner) return "🏆";
      if (status === 'ongoing') return "🔴";
      if (status === 'pending') return "⏳";
      if (status === 'waiting') return "⏸️";
      return "⏳";
    };

    const getTimeDetails = (match: any) => {
      const formatTime = (timeString: string) => {
        if (!timeString) return '';
        const date = new Date(timeString);
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      };

      const calculateEndTime = (startTime: string, durationStr: string) => {
        if (!startTime || !durationStr) return '';
        try {
          const start = new Date(startTime);
          const durationMinutes = parseInt(durationStr.toString().replace(/\D/g, '')) || 45;
          const end = new Date(start.getTime() + (durationMinutes * 60000));
          return formatTime(end.toISOString());
        } catch (error) {
          return '';
        }
      };

      const getStartTime = (completedAt: string, duration: string) => {
        if (!completedAt || !duration) return '';
        try {
          const end = new Date(completedAt);
          const durationMinutes = parseInt(duration.toString().replace(/\D/g, '')) || 45;
          const start = new Date(end.getTime() - (durationMinutes * 60000));
          return formatTime(start.toISOString());
        } catch (error) {
          return '';
        }
      };

      if (match.status === 'completed') {
        return {
          startTime: {
            time: getStartTime(match.completedAt, match.duration),
            label: 'Started',
            className: 'text-green-600'
          },
          endTime: {
            time: formatTime(match.completedAt),
            label: 'Finished',
            className: 'text-green-600'
          },
          duration: {
            time: match.duration || '45 min',
            label: 'Total Time',
            className: 'text-green-600'
          }
        };
      } else if (match.status === 'ongoing') {
        return {
          startTime: {
            time: formatTime(match.startedAt),
            label: 'Started',
            className: 'text-blue-600'
          },
          endTime: {
            time: 'Live',
            label: 'In Progress',
            className: 'text-blue-600'
          },
          duration: {
            time: match.expectedDuration || '~15 min left',
            label: 'Remaining',
            className: 'text-blue-600'
          }
        };
      } else if (match.status === 'pending') {
        return {
          startTime: {
            time: formatTime(match.scheduledAt),
            label: 'Scheduled',
            className: 'text-yellow-600'
          },
          endTime: {
            time: calculateEndTime(match.scheduledAt, '45'),
            label: 'Expected End',
            className: 'text-yellow-600'
          },
          duration: {
            time: match.expectedStart || 'Soon',
            label: 'Starts',
            className: 'text-yellow-600'
          }
        };
      } else if (match.status === 'waiting') {
        return {
          startTime: {
            time: formatTime(match.scheduledAt),
            label: 'Planned',
            className: 'text-gray-500'
          },
          endTime: {
            time: 'TBD',
            label: 'Awaiting',
            className: 'text-gray-500'
          },
          duration: {
            time: 'Waiting',
            label: match.expectedStart || 'Dependencies',
            className: 'text-gray-500'
          }
        };
      }
      return null;
    };

    const formatPlayerDisplay = (player: any, isWinner: boolean, isLeading: boolean) => {
      if (!player) return { name: 'TBD', details: 'Awaiting winner', className: 'text-gray-400 italic' };
      
      const rank = getPlayerRank(player);
      const rankIcon = getRankIcon(rank || 0);
      
      let className = 'text-gray-800';
      if (isWinner) className = 'font-bold text-green-700';
      else if (isLeading) className = 'font-semibold text-blue-700';
      
      return {
        name: player.name,
        details: `${rankIcon} Rank #${rank} • Rating: ${player.rating}`,
        className
      };
    };
    
    console.log('renderTable - currentMatches:', currentMatches);
    
    return (
      <div className="matches-container">
        {currentMatches.map((match: any, index: number) => {
          console.log('Rendering match:', match);
          console.log('match.player1:', match.player1);
          console.log('match.player2:', match.player2);
          
          const player1Rank = getPlayerRank(match.player1);
          const player2Rank = getPlayerRank(match.player2);
          
          return (
            <div key={match.id} className={`match-card match-${match.status}`}>
              {/* Match Header */}
              <div className="match-header">
                <div className="match-number">Match #{match.sno}</div>
                <div className="match-status">
                  {getStatusBadge(match.status)}
                </div>
              </div>
              
              {/* Player Cards */}
              <div className="players-container">
                    {/* Player 1 Card */}
                    <div 
                      className={`player-card ${match.winner?.id === match.player1?.id ? 'winner' : ''}`}
                      onClick={() => handlePlayerClick(match.player1)}
                    >
                      {!match.player1 && (
                        <div style={{ color: 'red', fontSize: '12px' }}>
                          Player 1 is null/undefined
                        </div>
                      )}
                  {match.player1 ? (
                    <>
                      <div className="player-avatar">
                        <img 
                          src={match.player1.profilePicture} 
                          alt={match.player1.name}
                          className="avatar-image"
                        />
                        <div className="avatar-popup">
                          <img 
                            src={match.player1.profilePicture} 
                            alt={match.player1.name}
                          />
                        </div>
                      </div>
                      <div className="player-info">
                        <div className="player-name">{match.player1.name}</div>
                        <div className="player-rank">
                          <span className="rank-icon">
                            {player1Rank === 1 ? '🥇' : '👤'}
                          </span>
                          <span>Rank #{player1Rank} • Rating: {match.player1.rating}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="player-info">
                      <div className="player-name">TBD</div>
                      <div className="player-rank">
                        <span>Waiting for Round 1 results</span>
                      </div>
                    </div>
                  )}
                  {match.winner?.id === match.player1?.id && (
                    <div className="winner-badge">🏆 Winner</div>
                  )}
                </div>
                
                {/* VS Separator */}
                <div className="vs-separator">
                  <div className="vs-text">VS</div>
                </div>
                
                    {/* Player 2 Card */}
                    <div 
                      className={`player-card ${match.winner?.id === match.player2?.id ? 'winner' : ''}`}
                      onClick={() => handlePlayerClick(match.player2)}
                    >
                      {match.player2 ? (
                        <>
                          <div className="player-avatar">
                            <img 
                              src={match.player2.profilePicture} 
                              alt={match.player2.name}
                              className="avatar-image"
                            />
                            <div className="avatar-popup">
                              <img 
                                src={match.player2.profilePicture} 
                                alt={match.player2.name}
                              />
                            </div>
                          </div>
                          <div className="player-info">
                            <div className="player-name">{match.player2.name}</div>
                            <div className="player-rank">
                              <span className="rank-icon">
                                {player2Rank === 1 ? '🥇' : '👤'}
                              </span>
                              <span>Rank #{player2Rank} • Rating: {match.player2.rating}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="player-info">
                          <div className="player-name">TBD</div>
                          <div className="player-rank">
                            <span>Waiting for Round 1 results</span>
                          </div>
                        </div>
                      )}
                  {match.winner?.id === match.player2?.id && (
                    <div className="winner-badge">🏆 Winner</div>
                  )}
                </div>
              </div>
              
              {/* Match Details */}
              <div className="match-details">
                <div className="details-grid">
                  {/* Status Column */}
                  <div className="detail-item">
                    <div className="detail-label">Status</div>
                    <div className="detail-value">
                      {getStatusBadge(match.status)}
                    </div>
                  </div>
                  
                  {/* Start Time Column */}
                  <div className="detail-item">
                    <div className="detail-label">Start Time</div>
                    <div className="detail-value">
                      {(() => {
                        const timeDetails = getTimeDetails(match);
                        return timeDetails?.startTime ? (
                          <div>
                            <div className={`time-value ${timeDetails.startTime.className}`}>
                              {timeDetails.startTime.time}
                            </div>
                            <div className="time-label">
                              {timeDetails.startTime.label}
                            </div>
                          </div>
                        ) : '-';
                      })()}
                    </div>
                  </div>
                  
                  {/* End Time Column */}
                  <div className="detail-item">
                    <div className="detail-label">End Time</div>
                    <div className="detail-value">
                      {(() => {
                        const timeDetails = getTimeDetails(match);
                        return timeDetails?.endTime ? (
                          <div>
                            <div className={`time-value ${timeDetails.endTime.className}`}>
                              {timeDetails.endTime.time}
                            </div>
                            <div className="time-label">
                              {timeDetails.endTime.label}
                            </div>
                          </div>
                        ) : '-';
                      })()}
                    </div>
                  </div>
                  
                  {/* Duration Column */}
                  <div className="detail-item">
                    <div className="detail-label">Duration</div>
                    <div className="detail-value">
                      {(() => {
                        const timeDetails = getTimeDetails(match);
                        return timeDetails?.duration ? (
                          <div>
                            <div className={`time-value ${timeDetails.duration.className}`}>
                              {timeDetails.duration.time}
                            </div>
                            <div className="time-label">
                              {timeDetails.duration.label}
                            </div>
                          </div>
                        ) : '-';
                      })()}
                    </div>
                  </div>
                  
                  {/* Score Column */}
                  <div className="detail-item">
                    <div className="detail-label">Score</div>
                    <div className="detail-value">
                      {match.winner ? (
                        <div className="match-result">
                          <span className="result-text">Final: {match.score}</span>
                        </div>
                      ) : match.status === 'ongoing' ? (
                        <div className="match-result live">
                          <span className="result-text">Live: {match.score}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Get tournament statistics
  const getStats = () => {
    const allMatches = [...matches.round1, ...matches.round2, ...matches.round3, ...matches.round4, ...matches.round5];
    const completed = allMatches.filter(m => m.status === 'completed').length;
    const ongoing = allMatches.filter(m => m.status === 'ongoing').length;
    const pending = allMatches.filter(m => m.status === 'pending').length;
    const waiting = allMatches.filter(m => m.status === 'waiting').length;
    
    return { completed, ongoing, pending, waiting };
  };

  const stats = getStats();

  console.log('About to render JSX...');
  
  return (
    <div className="tournament-container">
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Tournament Header */}
        <div className="tournament-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <h1 className="tournament-title">{tournamentInfo.name}</h1>
              <div className="tournament-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Calendar style={{ width: '16px', height: '16px' }} />
                  {new Date(tournamentInfo.date).toLocaleDateString()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MapPin style={{ width: '16px', height: '16px' }} />
                  {tournamentInfo.location}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Users style={{ width: '16px', height: '16px' }} />
                  {tournamentInfo.totalPlayers} Players
                </div>
              </div>
            </div>
            <div>
              <span style={{ 
                background: '#dbeafe', 
                color: '#1e40af', 
                padding: '4px 12px', 
                borderRadius: '9999px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>
                Tournament in Progress
              </span>
            </div>
          </div>
        </div>

        {/* Round Navigation */}
        <div className="round-navigation">
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
            Tournament Bracket - All Rounds
          </h2>
          <div className="round-tabs">
            {[1, 2, 3, 4, 5].map(round => (
              <button
                key={round}
                onClick={() => {
                  console.log('Round button clicked:', round);
                  setSelectedRound(round);
                }}
                className={`round-tab ${selectedRound === round ? 'active' : ''}`}
              >
                {getRoundName(round)}
                <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                  ({getCurrentRoundMatches().length})
                </span>
              </button>
            ))}
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#6b7280', 
            background: '#f9fafb', 
            padding: '12px', 
            borderRadius: '6px' 
          }}>
            <strong>{getRoundName(selectedRound)}:</strong> {getRoundDescription(selectedRound)}
          </div>
        </div>

        {/* Excel-style Table View */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px' 
          }}>
            {getRoundName(selectedRound)}
          </h2>
          {renderTable()}
        </div>

        {/* Live Tournament Statistics */}
        <div className="stats-section">
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '16px' 
          }}>
            Complete Tournament Overview
          </h2>
          <div className="stats-grid">
            <div className="stat-card stat-completed">
              <div className="stat-number" style={{ color: '#16a34a' }}>{stats.completed}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card stat-ongoing">
              <div className="stat-number" style={{ color: '#2563eb' }}>{stats.ongoing}</div>
              <div className="stat-label">Live Now</div>
            </div>
            <div className="stat-card stat-pending">
              <div className="stat-number" style={{ color: '#d97706' }}>{stats.pending}</div>
              <div className="stat-label">Scheduled</div>
            </div>
            <div className="stat-card stat-waiting">
              <div className="stat-number" style={{ color: '#6b7280' }}>{stats.waiting}</div>
              <div className="stat-label">Awaiting</div>
            </div>
          </div>
          
          {/* Tournament Progress Bar */}
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${(stats.completed / (stats.completed + stats.ongoing + stats.pending + stats.waiting)) * 100}%` }}
            ></div>
          </div>
          <div className="progress-text">
            Tournament Progress: {Math.round((stats.completed / (stats.completed + stats.ongoing + stats.pending + stats.waiting)) * 100)}% Complete
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfileModal && selectedPlayer && (
        <div className="profile-modal" onClick={closeProfileModal}>
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="profile-header">
              <div className="profile-avatar">
                <img 
                  src={selectedPlayer.profilePicture} 
                  alt={selectedPlayer.name}
                  className="profile-avatar-image"
                />
              </div>
              <div className="profile-info">
                <h3>{selectedPlayer.name}</h3>
                <p>Rating: {selectedPlayer.rating}</p>
              </div>
            </div>

            <div className="profile-stats">
              <div className="stat-item">
                <div className="stat-value">{selectedPlayer.wins}</div>
                <div className="stat-label">Wins</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{selectedPlayer.losses}</div>
                <div className="stat-label">Losses</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{selectedPlayer.rating}</div>
                <div className="stat-label">Rating</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">
                  {Math.round((selectedPlayer.wins / (selectedPlayer.wins + selectedPlayer.losses)) * 100)}%
                </div>
                <div className="stat-label">Win Rate</div>
              </div>
            </div>

            <div className="profile-visibility">
              <div className="visibility-label">
                {selectedPlayer.profileVisibility === 'public' && '🌍 Public Profile'}
                {selectedPlayer.profileVisibility === 'friends' && '👥 Friends Only'}
                {selectedPlayer.profileVisibility === 'private' && '🔒 Private Profile'}
              </div>
              <div className="visibility-info">
                {selectedPlayer.profileVisibility === 'public' && 'Anyone can view this profile'}
                {selectedPlayer.profileVisibility === 'friends' && 'Only friends can view this profile'}
                {selectedPlayer.profileVisibility === 'private' && 'Only you can view this profile'}
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button 
                onClick={closeProfileModal}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacyModal && (
        <div className="privacy-modal" onClick={closePrivacyModal}>
          <div className="privacy-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="privacy-icon">
              🔒
            </div>
            <div className="privacy-message">
              {privacyMessage}
            </div>
            <div className="privacy-actions">
              <button 
                onClick={closePrivacyModal}
                className="privacy-close-btn"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchDetails;
